import React from "react";
import EngineeringIcon from "../EngineeringIcons";
import AnimatedDivider from "../AnimatedDivider";

const SustainableBuilding: React.FC = () => {
  const buildingSolutions = [
    {
      title: "Interlocking Bricks",
      description: "Innovative interlocking brick systems for sustainable and cost-effective construction.",
      icon: "🧱",
      features: ["No mortar required", "Faster construction", "Cost effective"]
    },
    {
      title: "Eco-Friendly Materials",
      description: "Sustainable building materials that reduce environmental impact and improve efficiency.",
      icon: "🌿",
      features: ["Recycled materials", "Low carbon footprint", "Energy efficient"]
    },
    {
      title: "Green Building Design",
      description: "Sustainable architectural design that maximizes energy efficiency and environmental benefits.",
      icon: "🏗️",
      features: ["Passive solar design", "Natural ventilation", "Green roofs"]
    },
    {
      title: "Smart Building Systems",
      description: "Intelligent building systems that optimize energy use and occupant comfort.",
      icon: "🏠",
      features: ["Automated systems", "Energy monitoring", "Climate control"]
    }
  ];

  const services = [
    "Sustainable building design and engineering",
    "Interlocking brick manufacturing",
    "Green building certification",
    "Energy-efficient construction",
    "Building automation systems",
    "Maintenance and retrofitting"
  ];

  const benefits = [
    {
      title: "Cost Savings",
      description: "Reduced construction and operational costs through efficient design",
      icon: "💰"
    },
    {
      title: "Environmental Impact",
      description: "Minimal environmental footprint and sustainable resource use",
      icon: "🌍"
    },
    {
      title: "Energy Efficiency",
      description: "Significantly reduced energy consumption and utility costs",
      icon: "⚡"
    },
    {
      title: "Durability",
      description: "Long-lasting construction that requires minimal maintenance",
      icon: "🛡️"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-emerald-600 to-emerald-700 text-white py-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex justify-center mb-6">
              <div className="animate-pulley-bounce">
                <EngineeringIcon type="gear" size="lg" className="text-white" />
              </div>
            </div>
            <h1 className="text-5xl font-bold mb-6">Sustainable Building</h1>
            <p className="text-xl mb-8">
              Innovative construction solutions including interlocking bricks and eco-friendly building materials. 
              Creating sustainable, durable, and cost-effective structures.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <span className="bg-white/20 px-4 py-2 rounded-full">Green Construction</span>
              <span className="bg-white/20 px-4 py-2 rounded-full">Eco-Friendly Materials</span>
              <span className="bg-white/20 px-4 py-2 rounded-full">Smart Buildings</span>
            </div>
          </div>
        </div>
      </section>

      <AnimatedDivider variant="gears" />

      {/* Building Solutions */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Our Sustainable Building Solutions</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Comprehensive sustainable building solutions designed for efficiency, durability, and environmental responsibility.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {buildingSolutions.map((solution, index) => (
              <div key={index} className="bg-white rounded-lg shadow-lg p-8 engineering-card">
                <div className="flex items-center mb-6">
                  <div className="text-4xl mr-4 animate-bounce-slow">{solution.icon}</div>
                  <h3 className="text-2xl font-bold text-gray-800">{solution.title}</h3>
                </div>
                <p className="text-gray-600 mb-6">{solution.description}</p>
                <ul className="space-y-2">
                  {solution.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-gray-700">
                      <EngineeringIcon type="bolt" size="sm" className="text-emerald-500 mr-3" animated={false} />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <AnimatedDivider variant="tools" />

      {/* Benefits */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Benefits of Sustainable Building</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Our sustainable building solutions deliver multiple benefits for your projects and the environment.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center p-6 bg-gray-50 rounded-lg hover:bg-emerald-50 transition-colors">
                <div className="text-4xl mb-4 animate-pulley-bounce">{benefit.icon}</div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">{benefit.title}</h3>
                <p className="text-gray-600 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <AnimatedDivider variant="mixed" />

      {/* Services */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Our Services</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Comprehensive sustainable building services to meet all your green construction needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-center">
                  <EngineeringIcon type="wrench" size="md" className="text-emerald-500 mr-4" animated={false} />
                  <span className="font-semibold text-gray-800">{service}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-emerald-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Build a Sustainable Future</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Join the green building revolution with our innovative sustainable construction solutions. 
            Let's build a better future for Ekumfiman with eco-friendly, efficient, and durable structures.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-emerald-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center gap-2">
              <EngineeringIcon type="gear" size="sm" animated={false} />
              Start Your Project
            </button>
            <button className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-emerald-600 transition-colors flex items-center justify-center gap-2">
              <EngineeringIcon type="bolt" size="sm" />
              Learn More
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SustainableBuilding; 